import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface ApplovinConfig {
  eventKey: string;
  isEnabled?: boolean;
}

// Define Axon interface
interface AxonFunction {
  (...args: any[]): void;
  performOperation?: (...args: any[]) => void;
  operationQueue: any[];
  ts: number;
  eventKey: string;
}

// Window interface已在index.d.ts中统一定义

export class ApplovinProvider implements Provider {
  type = ProviderType.APPLOVIN;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: ApplovinConfig;

  constructor(config: ApplovinConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Initialize AppLovin Axon tracking
      const AXON_EVENT_KEY = this.config.eventKey;

      // AppLovin Axon initialization script
      const axonScripts = ['https://s.axon.ai/pixel.js', 'https://c.albss.com/p/l/loader.iife.js'];

      if (!window.axon) {
        const axon = function () {
          if ((axon as any).performOperation) {
            // eslint-disable-next-line prefer-rest-params
            (axon as any).performOperation.apply(axon, arguments);
          } else {
            // eslint-disable-next-line prefer-rest-params
            (axon as any).operationQueue.push(arguments);
          }
        } as AxonFunction;

        axon.operationQueue = [];
        axon.ts = Date.now();
        axon.eventKey = AXON_EVENT_KEY;

        window.axon = axon;

        const firstScript = document.getElementsByTagName('script')[0];

        for (let i = 0; i < axonScripts.length; i++) {
          const script = document.createElement('script');
          script.async = true;
          script.src = axonScripts[i];
          firstScript.parentNode.insertBefore(script, firstScript);
        }
      }

      // Initialize axon
      window.axon('init');

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize AppLovin:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    if (this.loggingEnabled) {
      logger.log('AppLovin tracking event:', event);
    }
    try {
      if (window.axon) {
        // Track event with AppLovin Axon
        window.axon(event.commandType || 'track', event.eventName, event.properties);
      }
    } catch (error) {
      logger.error('AppLovin tracking error:', error);
    }
  }

  /**
   * 启用AppLovin日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用AppLovin日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
